<script setup>
import { ref } from 'vue';

const player = ref(null);

defineProps({
    msg: String,
});

const USE_PRESET_SELECTOR = true;
const SHOW_GENRE = false;
</script>

<template>
    <div class="root">
        <PresetSelector
            v-if="USE_PRESET_SELECTOR"
            :selected-preset="selectedPreset"
            :previous-preset="previousPreset"
            :next-preset="nextPreset"
            @preset-click="onPresetClick"
            @preset-hover="onPresetHover"
            @preset-hover-out="onPresetHoverOut"
        />

        <div id="preset-genre" v-if="SHOW_GENRE">{{ (hoveredPreset || selectedPreset)?.selection ?? '' }}</div>
        <div id="preset-name">{{ (hoveredPreset || selectedPreset)?.name ?? '' }}</div>

        <div class="player-container">
            <Player
                ref="player"
                :presets="presets"
                :selected-preset="selectedPreset"
                @previous-preset-click="onPreviousNextPresetButtonClick(-1)"
                @next-preset-click="onPreviousNextPresetButtonClick(1)"
            />
        </div>
    </div>
</template>

<style lang="scss" scoped>
.root {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.player-container {
    width: 100%;
    margin-top: 20px;
}

#preset-name {
    font-size: 30px;
    font-weight: 700;
    line-height: 1.1;
}

#preset-genre {
    font-size: 40px;
    font-weight: 700;
    text-transform: uppercase;
    line-height: 1.1;
    margin-top: 10px;
}

</style>

<script>
import { presets, getPresets } from './data/presets';
import Player from './components/Player.vue';
import PresetSelector from './components/PresetSelector.vue';

const DYNAMIC_PRESET_LOADING = false;

export default {
    components: {
        Player, PresetSelector
    },
    created() {
        window.addEventListener('message', this.onMessage.bind(this), false);
    },
    destroyed() {
        window.removeEventListener('message', this.onMessage, false);
    },
    async mounted() {
        if (DYNAMIC_PRESET_LOADING) {
            this.presets.length = 0;
            this.presets.push(...await getPresets())
        }

        this.selectedPreset = this.presets[0];
        this.$refs.player.loadPreset(this.selectedPreset);
    },
    data() {
        return {
            presets,
            selectedPreset: null,
            hoveredPreset: null,
        };
    },
    computed: {
        previousPreset: {
            get() {
                return this.getPreviousOrNextPreset(-1);
            }
        },
        nextPreset: {
            get() {
                return this.getPreviousOrNextPreset(1);
            }
        },
    },
    methods: {
        getPreviousOrNextPreset(offset) {
            let presetIndex = this.presets.indexOf(this.selectedPreset);
            presetIndex += offset;

            if (presetIndex < 0) {
                presetIndex = this.presets.length - 1;
            } else if (presetIndex >= this.presets.length) {
                presetIndex = 0;
            }

            return this.presets[presetIndex];
        },
        onPreviousNextPresetButtonClick(offset) {
            this.selectedPreset = this.getPreviousOrNextPreset(offset);
            this.$refs.player.loadPreset(this.selectedPreset);
            this.postPresetChangeMessage(this.selectedPreset);
        },
        onPresetClick(preset) {
            this.selectedPreset = preset;
            this.$refs.player.loadPreset(this.selectedPreset, true);
            this.postPresetChangeMessage(preset);
        },
        onPresetHover(preset) {
            this.hoveredPreset = preset;
        },
        onPresetHoverOut(genreIndex, presetIndex) {
            this.hoveredPreset = null;
        },
        postPresetChangeMessage(preset) {
            if (window.parent) {
                window.parent.postMessage({
                    message: 'presetChange',
                    presetName: preset.name
                }, "*");
            }
        },
        onMessage(event) {
            if (event.data?.message === 'loadPreset') {
                const preset = this.presets.find((preset) => preset.name === event.data.presetName);
                if (preset) {
                    this.selectedPreset = preset;
                    this.$refs.player.loadPreset(this.selectedPreset, true);
                } else {
                    console.log(`Preset ${event.data.presetName} not found.`);
                }
            }
        }
    },
};
</script>